import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUIStore } from '@/stores/uiStore';
import { cn } from '@/lib/utils';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import type { Vendor } from '@/types/api';
import { createVendor, fetchVendors, updateVendor } from '@/lib/api/vendors';
import { EditorLoadingPane } from './EditorLoadingPane';
import { MobileEditorDrawer } from './MobileEditorDrawer';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const vendorFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(255),
  notes: z.string().max(5000).optional(),
  isActive: z.boolean(),
});

type VendorFormValues = z.infer<typeof vendorFormSchema>;
type VendorStatusFilter = 'active' | 'inactive' | 'all';

export function VendorLibrarySection() {
  const { showToast } = useUIStore();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const { searchQuery, debouncedSearch, setSearchQuery, resetSearch } = useDebouncedSearch();
  const [statusFilter, setStatusFilter] = useState<VendorStatusFilter>('active');
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const latestRequestIdRef = useRef(0);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const loadVendors = useCallback(async () => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    try {
      setLoading(true);
      const response = await fetchVendors({
        search: debouncedSearch || undefined,
        isActive:
          statusFilter === 'all'
            ? undefined
            : statusFilter === 'active',
      });
      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      setVendors(response);
    } catch (error) {
      if (requestId !== latestRequestIdRef.current) {
        return;
      }

      showToast(
        error instanceof Error ? error.message : 'Failed to load vendors',
        'error'
      );
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [debouncedSearch, showToast, statusFilter]);

  useEffect(() => {
    void loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    if (isDesktop && mobileEditorOpen) {
      setMobileEditorOpen(false);
    }
  }, [isDesktop, mobileEditorOpen]);

  useEffect(() => {
    if (creating) return;

    if (vendors.length === 0) {
      setSelectedVendorId(null);
      setMobileEditorOpen(false);
      return;
    }

    if (!selectedVendorId || !vendors.some((vendor) => vendor.id === selectedVendorId)) {
      const firstVendor = vendors[0];
      if (firstVendor) {
        setSelectedVendorId(firstVendor.id);
      }
    }
  }, [creating, selectedVendorId, vendors]);

  const selectedVendor = useMemo(
    () => vendors.find((vendor) => vendor.id === selectedVendorId) ?? null,
    [selectedVendorId, vendors]
  );

  const startCreate = () => {
    setCreating(true);
    setSelectedVendorId(null);
    setMobileEditorOpen(true);
  };

  const selectVendor = (vendorId: number) => {
    setCreating(false);
    setSelectedVendorId(vendorId);
    setMobileEditorOpen(true);
  };

  const closeEditor = () => {
    setCreating(false);
    setMobileEditorOpen(false);
  };

  const handleVendorSaved = async (savedVendor: Vendor, mode: 'create' | 'edit') => {
    if (mode === 'create') {
      resetSearch();
      setStatusFilter('active');
      setCreating(false);
      setSelectedVendorId(savedVendor.id);
    }

    await loadVendors();
    setMobileEditorOpen(true);
  };

  const shouldShowLoadingPane = !creating && !selectedVendor && (loading || vendors.length > 0);
  const mobileEditorTitle = creating ? 'Create Vendor' : selectedVendor?.name ?? 'Vendor details';
  const editorPane = shouldShowLoadingPane ? (
    <EditorLoadingPane
      title="Loading vendor"
      description="Fetching the first vendor for this view."
    />
  ) : (
    <VendorEditorPane
      vendor={creating ? null : selectedVendor}
      onSaved={handleVendorSaved}
      showHeader={isDesktop}
    />
  );

  return (
    <>
      <div className="grid gap-4 md:grid-cols-[minmax(260px,2fr)_minmax(200px,3fr)]">
        <Card className="flex min-h-[620px]">
          <CardHeader className="space-y-4 border-b">
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle>Vendors</CardTitle>
                <CardDescription>
                  {loading ? 'Loading vendors...' : `${vendors.length} vendor${vendors.length === 1 ? '' : 's'}`}
                </CardDescription>
              </div>
              <Button size="sm" onClick={startCreate}>
                <Plus className="h-4 w-4" />
                New
              </Button>
            </div>

            <div className="space-y-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search vendors..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="pl-9"
                />
              </div>

              <div className="grid gap-3">
                <Select
                  value={statusFilter}
                  onValueChange={(value) => setStatusFilter(value as VendorStatusFilter)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>

          <CardContent className="flex-1 overflow-y-auto px-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />
              </div>
            ) : vendors.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <p className="mb-4 text-sm text-muted-foreground">
                  {debouncedSearch ? `No vendors found matching "${debouncedSearch}"` : 'No vendors found'}
                </p>
                <Button size="sm" onClick={startCreate}>Create First Vendor</Button>
              </div>
            ) : (
              <div className="px-4 py-3">
                <div className="mb-2 grid grid-cols-[minmax(0,1fr)] gap-3 px-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  <span>Name</span>
                </div>

                <div className="space-y-2" role="listbox" aria-label="Vendors">
                  {vendors.map((vendor) => {
                    const isSelected = !creating && selectedVendorId === vendor.id;

                    return (
                      <div
                        key={vendor.id}
                        role="option"
                        aria-selected={isSelected}
                        className={cn(
                          'grid grid-cols-[minmax(0,1fr)] items-center gap-3 rounded-lg border px-2 py-2 transition-colors',
                          isSelected ? 'border-primary/40 bg-primary/5' : 'hover:bg-muted/60',
                          vendor.isActive ? '' : 'opacity-60'
                        )}
                      >
                        <button
                          type="button"
                          className="min-w-0 text-left"
                          onClick={() => selectVendor(vendor.id)}
                        >
                          <p className="truncate font-medium text-foreground" title={vendor.name}>
                            {vendor.name}
                          </p>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {isDesktop ? editorPane : null}

        {!isDesktop ? (
          <MobileEditorDrawer
            open={mobileEditorOpen}
            onOpenChange={setMobileEditorOpen}
            onClose={closeEditor}
            title={mobileEditorTitle}
          >
            {editorPane}
          </MobileEditorDrawer>
        ) : null}
      </div>
    </>
  );
}

interface VendorEditorPaneProps {
  vendor: Vendor | null;
  onSaved: (vendor: Vendor, mode: 'create' | 'edit') => Promise<void>;
  showHeader?: boolean;
}

function VendorEditorPane({
  vendor,
  onSaved,
  showHeader = true,
}: VendorEditorPaneProps) {
  const { showToast } = useUIStore();
  const [saving, setSaving] = useState(false);
  const isEditMode = !!vendor;

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<VendorFormValues>({
    resolver: zodResolver(vendorFormSchema),
    defaultValues: buildVendorDefaults(vendor),
  });

  useEffect(() => {
    reset(buildVendorDefaults(vendor));
  }, [vendor, reset]);

  const isActive = watch('isActive');

  const onSubmit = async (values: VendorFormValues) => {
    try {
      setSaving(true);

      const payload = {
        name: values.name.trim(),
        notes: values.notes?.trim() || null,
        isActive: values.isActive,
      };

      const savedVendor = isEditMode && vendor
        ? await updateVendor(vendor.id, payload)
        : await createVendor(payload);

      showToast(
        isEditMode ? 'Vendor updated successfully' : 'Vendor created successfully',
        'success'
      );

      await onSaved(savedVendor, isEditMode ? 'edit' : 'create');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : `Failed to ${isEditMode ? 'update' : 'create'} vendor`,
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="min-h-[620px]">
      {showHeader ? (
        <CardHeader className="border-b">
          <CardTitle>{isEditMode ? vendor.name : 'Create Vendor'}</CardTitle>
        </CardHeader>
      ) : null}

      <CardContent className="flex-1 overflow-y-auto">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="vendor-name">Vendor Name</Label>
            <Input
              id="vendor-name"
              {...register('name')}
              placeholder="e.g. Costco Business Center"
              disabled={saving}
            />
            {errors.name ? <p className="text-sm text-destructive">{errors.name.message}</p> : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="vendor-notes">Notes</Label>
            <Textarea
              id="vendor-notes"
              {...register('notes')}
              placeholder="Optional sourcing notes..."
              rows={5}
              disabled={saving}
            />
            {errors.notes ? <p className="text-sm text-destructive">{errors.notes.message}</p> : null}
          </div>

          <div className="rounded-lg border bg-muted/30 px-4 py-3">
            <div className="rounded-lg border bg-muted/30 px-4 py-3 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(event) => setValue('isActive', event.target.checked, { shouldDirty: true })}
                  className="h-4 w-4 rounded border-gray-300"
                  disabled={saving}
                />
                Active for future ingredient assignments
              </label>
              {isEditMode ? (
                <p className="text-xs text-muted-foreground pl-6">
                  {vendor?.ingredientCount ?? 0} ingredient{vendor?.ingredientCount === 1 ? '' : 's'} currently reference this vendor.
                </p>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-2 border-t pt-4">
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Vendor'}
              </Button>
            </div>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function buildVendorDefaults(vendor: Vendor | null): VendorFormValues {
  return {
    name: vendor?.name ?? '',
    notes: vendor?.notes ?? '',
    isActive: vendor?.isActive ?? true,
  };
}
